import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  PoTableColumn, PoTableAction, PoTableColumnSort, PoPageAction, PoSelectOption, PoTableComponent,
  PoPageFilter, PoDisclaimerGroup, PoDisclaimer, PoModalAction,
  PoPageListComponent, PoModalComponent
} from '@po-ui/ng-components';
import { EMPTY, of, Subject } from 'rxjs';
import { catchError, concatMap, expand, finalize, takeUntil, takeWhile } from 'rxjs/operators';
import { timer } from 'rxjs';
import { Router } from '@angular/router';
import { commitResourceLabel, pullResourceLabel } from './pull-resource-id.util';
import { resourceTypeLabel } from '../core/utils/resource-type-label.util';
import { SOURCE_PROVIDER_OPTIONS, sourceProviderLabel } from '../core/utils/provider-label.util';

import { NotificationServiceHM } from '../../notification-item/notification-item.service';
import { ReviewSortBy, ReviewService, ReviewFilters } from './review.service';
import { JobsService } from '../core/api/jobs.service';
import { SmartReviewPermissionsService } from '../core/services/smart-review-permissions.service';
import { reviewOpenBlock } from './utils/review-open.util';
import { Review, SmartReviewJobFull } from './interfaces/review.interface';
import { MainService } from 'src/app/app.service';
import { SendReviewModalComponent } from './components/send-review-modal/send-review-modal.component';
import { RetryJobModalComponent, RetryRequest } from './components/retry-job-modal/retry-job-modal.component';
import { TERMINAL_STATUSES, FAILED_STATUSES } from './review-constants';
import { buildReviewDeleteKey, getDeleteReviewErrorMessage, getDeleteReviewHttpStatus } from './utils/review-delete.util';
import { getApiErrorMessage, isServiceUnavailable, showServiceUnavailableToast } from '../core/utils/http-error-message.util';
import { formatDateTimePtBr } from '../core/utils/date-format.util';
import {
  UI_PREF_KEYS, clearPref, hasPref, readPref, sanitizeString, sanitizeStringArray, writePref
} from '../core/utils/ui-prefs.util';
import { applyColumnsPref, sanitizeColumnsPref, toColumnsPref } from '../core/utils/table-columns-pref.util';
import { PERIOD_PRESET_OPTIONS, Period, rehydratePeriod } from '../core/utils/period.util';
import { PeriodFilterComponent } from 'src/app/shared/period/period-filter/period-filter.component';
const MAX_POLL_ATTEMPTS = 15;
/** Sem recorte de data: a lista abre como sempre abriu, com todos os pedidos. */
const ALL_PERIOD: Period = { preset: 'all', dateFrom: '', dateTo: '' };
const POLL_INTERVAL_FAST_MS = 10000;
const POLL_INTERVAL_MEDIUM_MS = 20000;
const POLL_INTERVAL_SLOW_MS = 30000;

const STATUS_BADGE_MAP: Record<string, string> = {
  done: 'success', failed: 'danger', dead: 'danger', processing: 'info', running: 'info'
};

@Component({
  selector: 'app-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewComponent implements OnInit, OnDestroy {
  @ViewChild('poPageList', { static: true }) poPageList!: PoPageListComponent;
  @ViewChild('advancedFilterModal', { static: true }) advancedFilterModal!: PoModalComponent;
  @ViewChild('sendModal') sendModal!: SendReviewModalComponent;
  @ViewChild('retryModal') retryModal!: RetryJobModalComponent;
  @ViewChild('deleteReviewModal', { static: true }) deleteReviewModal!: PoModalComponent;
  @ViewChild('deltaReviewModal', { static: true }) deltaReviewModal!: PoModalComponent;

  /**
   * A instância do po-table. `columns` dela é o array VIVO das colunas — o único lugar
   * onde o gerenciador continua escrevendo o alfinete depois do primeiro liga/desliga.
   * Ver `toColumnsPref`.
   */
  @ViewChild(PoTableComponent) poTable?: PoTableComponent;
  @ViewChild(PeriodFilterComponent) periodFilter?: PeriodFilterComponent;

  loading = false;
  reviews: Review[] = [];
  totalItems = 0;
  reviewPendingDelete: Review | null = null;
  deletingReview = false;
  deleteReviewPrimaryAction: PoModalAction = {
    label: 'Excluir',
    danger: true,
    action: () => this.confirmDeleteReview(),
    disabled: true
  };
  deleteReviewSecondaryAction: PoModalAction = {
    label: 'Cancelar',
    action: () => this.closeDeleteReviewModal()
  };
  reviewPendingDelta: Review | null = null;
  deltaReviewPrimaryAction: PoModalAction = {
    label: 'Revisar',
    action: () => this.confirmDeltaReview()
  };
  deltaReviewSecondaryAction: PoModalAction = {
    label: 'Cancelar',
    action: () => this.closeDeltaReviewModal()
  };

  // Filtros
  filters: ReviewFilters = {
    resourceTypes: [],
    sourceProviders: [],
    statuses: [],
    compareFrom: '',
    compareTo: '',
    requestedBy: '',
    period: { ...ALL_PERIOD },
    quickSearch: ''
  };

  advancedFilters: ReviewFilters = {
    resourceTypes: [],
    sourceProviders: [],
    statuses: [],
    compareFrom: '',
    compareTo: '',
    requestedBy: '',
    period: { ...ALL_PERIOD },
    quickSearch: ''
  };

  // Paginação / show-more
  currentPage = 1;
  pageSize = 25;
  loadingShowMore = false;

  // Ordenação
  sortBy: ReviewSortBy = 'requestedAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Opções para filtros
  resourceTypeOptions: PoSelectOption[] = [
    { label: 'Issue', value: 'issue' },
    { label: 'Comparison', value: 'comparison' },
    { label: 'Standalone', value: 'standalone' },
    { label: 'Changeset', value: 'changeset' },
    { label: 'Pull Request', value: 'pull' },
    { label: 'Commit', value: 'commit' },
    { label: 'Git Comparison', value: 'git-comparison' }
  ];

  readonly sourceProviderOptions: PoSelectOption[] = SOURCE_PROVIDER_OPTIONS;
  readonly allPeriod: Period = ALL_PERIOD;

  statusOptions: PoSelectOption[] = [
    { label: 'Queued', value: 'queued' },
    { label: 'Processing', value: 'processing' },
    { label: 'Done', value: 'done' },
    { label: 'Failed', value: 'failed' },
    { label: 'Dead', value: 'dead' }
  ];

  readonly filter: PoPageFilter = {
    action: this.onQuickSearch.bind(this),
    advancedAction: this.openAdvancedFilter.bind(this),
    placeholder: 'Buscar por nome, ID, issue...',
    width: 4
  };

  disclaimerGroup: PoDisclaimerGroup = {
    title: 'Filtros ativos',
    disclaimers: [],
    remove: this.onDisclaimerRemove.bind(this),
    removeAll: this.onRemoveAll.bind(this)
  };

  readonly advancedFilterApplyAction: PoModalAction = {
    label: 'Aplicar',
    action: () => this.applyAdvancedFilter()
  };

  readonly advancedFilterCancelAction: PoModalAction = {
    label: 'Cancelar',
    action: () => this.advancedFilterModal.close()
  };

  // Rastreia quais resourceIds já têm polling ativo para evitar duplicatas
  private pollingActive = new Set<string>();
  private deletingReviewKeys = new Set<string>();

  private destroy$ = new Subject<void>();
  
  private readonly sortFieldMap: Partial<Record<string, ReviewSortBy>> = {
    statusDerived: 'status',
    requestedBy: 'requestedBy',
    requestedAt: 'requestedAt',
    lastActivityAt: 'lastActivityAt'
  };

  /**
   * A definição da tela: rótulo, tipo e largura de cada coluna, na ordem pensada aqui.
   * O que o usuário mexeu no gerenciador entra por cima disso, em `columns`.
   */
  private readonly baseColumns: PoTableColumn[] = [
    { property: 'resourceType', label: 'Tipo', type: 'cellTemplate', width: '120px', sortable: false },
    { property: 'sourceProvider', label: 'Origem', type: 'cellTemplate', width: '150px', sortable: false },
    { property: 'displayName', label: 'Review', type: 'cellTemplate', sortable: false, width: '280px' },
    { property: 'statusDerived', label: 'Status', type: 'cellTemplate', width: '120px' },
    { property: 'currentStep', label: 'Passo', type: 'cellTemplate', width: '100px', sortable: false },
    { property: 'info', label: 'Info', type: 'cellTemplate', width: '60px', sortable: false },
    { property: 'requestedBy', label: 'Solicitado por', width: '175px' },
    { property: 'requestedAt', label: 'Solicitado em', type: 'cellTemplate', width: '155px' },
    { property: 'lastActivityAt', label: 'Última atividade', type: 'cellTemplate', width: '185px' }
  ];

  /**
   * As colunas que a tabela recebe. Montadas no `ngOnInit` a partir de `baseColumns` e
   * da preferência salva.
   *
   * Nunca reatribuída depois disso, com uma exceção só (`onColumnsRestore`): o po-table
   * guarda a ordem escolhida pelo usuário no array INTERNO dele (`onVisibleColumnsChange`
   * faz `this.columns = columns`), e uma reatribuição aqui chegaria como mudança de input
   * e desfaria essa ordem na tela — que é justamente o efeito desejado ao restaurar.
   */
  columns: PoTableColumn[] = [];

  // Montadas no ngOnInit já filtradas pelas capabilities: o `po-page-list` ignora
  // `visible` quando é função (filtra só por `visible !== false`), então uma ação
  // sem permissão continuaria aparecendo. Como as capabilities não mudam durante a
  // sessão, filtrar o array é mais simples e vale para page e table actions.
  pageActions: PoPageAction[] = [];
  tableActions: PoTableAction[] = [];

  private buildPageActions(): PoPageAction[] {
    const actions: PoPageAction[] = [
      {
        label: 'Atualizar',
        icon: 'po-icon-refresh',
        action: () => this.loadReviews()
      },
      {
        label: 'Dashboard',
        icon: 'po-icon-chart-columns',
        action: () => this.router.navigate(['/menu/smart-review/reviews/dashboard'])
      }
    ];

    if (this.canRun) {
      actions.push({
        label: 'Novo Review',
        icon: 'po-icon-plus',
        action: () => this.sendModal.open()
      });
    }

    return actions;
  }

  private buildTableActions(): PoTableAction[] {
    const actions: PoTableAction[] = [
      {
        label: 'Abrir Review',
        icon: 'po-icon-arrow-right',
        action: this.viewDetail.bind(this),
        disabled: this.cannotViewDetail.bind(this)
      }
    ];

    if (this.canRun) {
      actions.push(
        {
          label: 'Revisar novas alterações',
          icon: 'po-icon-edit',
          action: this.openDeltaReviewModal.bind(this),
          disabled: this.cannotDeltaReview.bind(this)
        },
        {
          label: 'Reprocessar',
          icon: 'po-icon-refresh',
          action: this.openRetryModal.bind(this),
          disabled: this.cannotRetry.bind(this)
        }
      );
    }

    actions.push({
      label: 'Excluir review',
      icon: 'po-icon-delete',
      type: 'danger',
      action: this.openDeleteReviewModal.bind(this),
      disabled: this.cannotDeleteReview.bind(this)
    });

    return actions;
  }

  constructor(
    private smartReviewService: ReviewService,
    private jobsService: JobsService,
    private mainService: MainService,
    private permissions: SmartReviewPermissionsService,
    private router: Router,
    public notificationServiceHM: NotificationServiceHM,
    private cdr: ChangeDetectorRef
  ) {}

  /** Disparo/reprocessamento de review exige `smartreview.run`. */
  get canRun(): boolean {
    return this.permissions.hasRun();
  }

  /**
   * Fotografa as colunas ao sair da tela — por navegação (`ngOnDestroy`) ou ao deixar a
   * página (`visibilitychange`).
   *
   * Necessário porque nem toda mudança do gerenciador emite `p-change-visible-columns`:
   * desafixar uma coluna não altera a lista de visíveis nem a ordem dela, então o evento
   * não dispara e o pino removido continuaria salvo, voltando no carregamento seguinte.
   * Aqui o estado vem do array vivo do po-table, que reflete tudo — inclusive o que o
   * evento cala. Ver `toColumnsPref`.
   *
   * O `visibilitychange` é o que cobre F5 e fechar a aba, justamente os casos em que o
   * pino teimoso apareceria: o Angular não roda `ngOnDestroy` quando a página é
   * descarregada, só quando o roteador troca de tela. É ele, e não `beforeunload`, porque
   * `beforeunload` não dispara de forma confiável no descarte de aba em iOS/Android.
   */
  /** Referência estável para dar e tirar do listener — arrow, para não perder o `this`. */
  private readonly saveColumnsPrefOnHide = (): void => {
    if (document.visibilityState === 'hidden') { this.saveColumnsPref(); }
  };

  private saveColumnsPref(): void {
    const key = UI_PREF_KEYS.reviewColumns;
    // Só atualiza o que já existe. Sem esta guarda, sair de uma tela nunca personalizada
    // gravaria o padrão de HOJE como preferência, e uma futura reordenação das colunas na
    // definição da tela nunca chegaria a quem nunca pediu nada.
    if (!this.poTable || !hasPref(key)) { return; }
    writePref(key, toColumnsPref(this.poTable.columns));
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.saveColumnsPrefOnHide);
    this.saveColumnsPref();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.mainService.validSession();
    this.pageActions = this.buildPageActions();
    this.tableActions = this.buildTableActions();
    this.columns = applyColumnsPref(
      this.baseColumns,
      readPref(UI_PREF_KEYS.reviewColumns, sanitizeColumnsPref)
    );
    document.addEventListener('visibilitychange', this.saveColumnsPrefOnHide);
    this.restoreFilters();
    this.loadReviews();
  }

  /**
   * Filtros salvos de volta para a tela. Cada campo passa pelo formato que ele tem aqui
   * — o conteúdo do navegador é texto editável, e um `statuses: "done"` (string onde se
   * espera array) quebraria a montagem dos chips na primeira renderização.
   *
   * Tipo e status ainda são conferidos contra as opções da tela: um valor que saiu do
   * enum numa versão anterior viraria um filtro que o backend não reconhece mais, com
   * um chip ativo que o usuário não consegue relacionar a nada.
   */
  private restoreFilters(): void {
    const saved = readPref(UI_PREF_KEYS.reviewFilters, (raw): Partial<ReviewFilters> | null => {
      if (!raw || typeof raw !== 'object') { return null; }
      const value = raw as Partial<Record<keyof ReviewFilters, unknown>>;
      return {
        resourceTypes: sanitizeStringArray(value.resourceTypes, this.resourceTypeOptions.map(o => String(o.value))),
        sourceProviders: sanitizeStringArray(value.sourceProviders, this.sourceProviderOptions.map(o => String(o.value))),
        statuses: sanitizeStringArray(value.statuses, this.statusOptions.map(o => String(o.value))),
        compareFrom: sanitizeString(value.compareFrom),
        compareTo: sanitizeString(value.compareTo),
        requestedBy: sanitizeString(value.requestedBy),
        // Preset relativo volta re-resolvido contra hoje — ver rehydratePeriod.
        period: rehydratePeriod(value.period) ?? { ...ALL_PERIOD }
      };
    });

    if (!saved) { return; }

    this.filters = { ...this.filters, ...saved };
    this.updateDisclaimers();
  }

  /**
   * Colunas mudaram no gerenciador: liga/desliga, setas e alfinete.
   *
   * O alfinete sozinho pode não chegar aqui — o po-table só emite quando a LISTA de
   * visíveis muda de conteúdo ou de ordem, e fixar a primeira coluna não muda nem uma
   * nem outra. Nesse caso o pino continua valendo na tela e é salvo junto com a próxima
   * alteração, porque `toColumnsPref` lê `fixed` dos objetos de coluna, que o
   * gerenciador altera na hora.
   */
  onColumnsChange(visibleProperties: string[]): void {
    writePref(
      UI_PREF_KEYS.reviewColumns,
      toColumnsPref(this.poTable?.columns ?? this.columns, visibleProperties)
    );
  }

  /**
   * "Restaurar padrão" no gerenciador: some com a preferência (não grava o padrão, que
   * congelaria a tela na versão de hoje) e devolve a tabela à definição da tela.
   *
   * A reatribuição de `columns` é necessária, e é a única exceção à regra de nunca
   * reatribuí-la: o po-table guarda como "padrão" o PRIMEIRO array que recebeu
   * (`initialColumns` só é preenchido quando está `undefined`), e no `ngOnInit` esse
   * array já é a preferência aplicada. Sem isto, "Restaurar padrão" apagaria o que está
   * salvo e devolveria a tabela à própria preferência apagada — tela e armazenamento
   * discordando até o próximo F5.
   */
  onColumnsRestore(): void {
    clearPref(UI_PREF_KEYS.reviewColumns);
    this.columns = applyColumnsPref(this.baseColumns, null);
  }

  onQuickSearch(term: string): void {
    this.filters.quickSearch = term;
    this.saveFilters();
    this.loadReviews();
    this.updateDisclaimers();
  }

  /**
   * O período vai como objeto NOVO: o conteúdo do po-modal sobrevive ao fechamento, e o
   * seletor só se reinicia quando a referência muda. Sem a cópia, um "Personalizado"
   * deixado pela metade e cancelado reapareceria na reabertura, sem estar valendo.
   */
  openAdvancedFilter(): void {
    this.advancedFilters = { ...this.filters, period: { ...(this.filters.period ?? ALL_PERIOD) } };
    this.advancedFilterModal.open();
  }

  applyAdvancedFilter(): void {
    // "Personalizado" é lido do que está no campo: o seletor só emite com as duas pontas,
    // e aplicar sem elas manteria o período anterior sem ninguém perceber.
    if (this.periodFilter?.isCustom) {
      const dateFrom = (this.periodFilter.range?.start ?? '').slice(0, 10);
      const dateTo = (this.periodFilter.range?.end ?? '').slice(0, 10);
      if (!dateFrom || !dateTo) {
        this.notificationServiceHM.showToast('Informe as datas inicial e final do período', 'warning');
        return;
      }
      this.advancedFilters.period = { preset: 'custom', dateFrom, dateTo };
    }
    this.filters = { ...this.advancedFilters, quickSearch: this.filters.quickSearch };
    this.saveFilters();
    this.advancedFilterModal.close();
    this.loadReviews();
    this.updateDisclaimers();
  }

  onDisclaimerRemove({ removedDisclaimer }: { removedDisclaimer: PoDisclaimer; currentDisclaimers: PoDisclaimer[] }): void {
    switch (removedDisclaimer.property) {
      case 'quickSearch':
        this.filters.quickSearch = '';
        this.poPageList.clearInputSearch();
        break;
      case 'resourceTypes': this.filters.resourceTypes = []; break;
      case 'sourceProviders': this.filters.sourceProviders = []; break;
      case 'statuses': this.filters.statuses = []; break;
      case 'requestedBy': this.filters.requestedBy = ''; break;
      case 'compareFrom': this.filters.compareFrom = ''; break;
      case 'compareTo': this.filters.compareTo = ''; break;
      case 'period': this.filters.period = { ...ALL_PERIOD }; break;
    }
    this.saveFilters();
    this.loadReviews();
    this.updateDisclaimers();
  }

  onRemoveAll(): void {
    this.filters = { resourceTypes: [], sourceProviders: [], statuses: [], compareFrom: '', compareTo: '', requestedBy: '', period: { ...ALL_PERIOD }, quickSearch: '' };
    clearPref(UI_PREF_KEYS.reviewFilters);
    this.poPageList.clearInputSearch();
    this.loadReviews();
    this.disclaimerGroup = { ...this.disclaimerGroup, disclaimers: [] };
  }

  /** A busca rápida fica de fora: é consulta pontual, não recorte que se quer de volta. */
  private saveFilters(): void {
    const { quickSearch, ...toSave } = this.filters;
    writePref(UI_PREF_KEYS.reviewFilters, toSave);
  }

  private updateDisclaimers(): void {
    const disclaimers: PoDisclaimer[] = [];

    if (this.filters.quickSearch) {
      disclaimers.push({ label: `Busca: ${this.filters.quickSearch}`, value: this.filters.quickSearch, property: 'quickSearch' });
    }
    if (this.filters.resourceTypes.length) {
      const labels = this.filters.resourceTypes
        .map(v => this.resourceTypeOptions.find(o => o.value === v)?.label ?? v).join(', ');
      disclaimers.push({ label: `Tipo: ${labels}`, value: this.filters.resourceTypes, property: 'resourceTypes' });
    }
    if (this.filters.sourceProviders.length) {
      const labels = this.filters.sourceProviders
        .map(v => this.sourceProviderOptions.find(o => o.value === v)?.label ?? v).join(', ');
      disclaimers.push({ label: `Origem: ${labels}`, value: this.filters.sourceProviders, property: 'sourceProviders' });
    }
    if (this.filters.statuses.length) {
      const labels = this.filters.statuses
        .map(v => this.statusOptions.find(o => o.value === v)?.label ?? v).join(', ');
      disclaimers.push({ label: `Status: ${labels}`, value: this.filters.statuses, property: 'statuses' });
    }
    if (this.filters.requestedBy) {
      disclaimers.push({ label: `Solicitado por: ${this.filters.requestedBy}`, value: this.filters.requestedBy, property: 'requestedBy' });
    }
    const periodLabel = this.periodLabel(this.filters.period);
    if (periodLabel) {
      disclaimers.push({ label: `Período: ${periodLabel}`, value: periodLabel, property: 'period' });
    }
    if (this.filters.compareFrom) {
      disclaimers.push({ label: `Changeset Origem: ${this.filters.compareFrom}`, value: this.filters.compareFrom, property: 'compareFrom' });
    }
    if (this.filters.compareTo) {
      disclaimers.push({ label: `Changeset Destino: ${this.filters.compareTo}`, value: this.filters.compareTo, property: 'compareTo' });
    }

    this.disclaimerGroup = { ...this.disclaimerGroup, disclaimers };
  }

  /** Rótulo do chip: o nome do preset relativo, ou as datas do intervalo personalizado. */
  private periodLabel(period?: Period): string {
    if (!period?.dateFrom && !period?.dateTo) { return ''; }
    if (period.preset !== 'custom') {
      const preset = PERIOD_PRESET_OPTIONS.find(o => o.value === period.preset);
      if (preset) { return preset.label; }
    }
    const toPtBr = (iso: string) => iso.split('-').reverse().join('/');
    return `${toPtBr(period.dateFrom)} a ${toPtBr(period.dateTo)}`;
  }

  get hasComparisonTypeSelected(): boolean {
    return this.advancedFilters.resourceTypes.includes('comparison');
  }
  
  onSortBy(sort: PoTableColumnSort): void {
    this.sortBy = this.sortFieldMap[sort.column?.property ?? ''] ?? 'requestedAt';
    this.sortOrder = sort.type === 'ascending' ? 'asc' : 'desc';
    this.loadReviews();
  }

  onShowMore(): void {
    this.loadingShowMore = true;
    this.currentPage++;
    const offset = (this.currentPage - 1) * this.pageSize;
    this.smartReviewService.getReviews(this.filters, this.pageSize, offset, this.sortBy, this.sortOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.reviews = [...this.reviews, ...this.withComputedFields(response.items || [])];
          this.totalItems = response.total || 0;
          this.loadingShowMore = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationServiceHM.showToast('Erro ao carregar mais reviews', 'error');
          this.currentPage--;
          this.loadingShowMore = false;
          this.cdr.markForCheck();
        }
      });
  }

  get allLoaded(): boolean {
    return this.reviews.length >= this.totalItems;
  }

  get pendingDeleteResourceTypeLabel(): string {
    if (!this.reviewPendingDelete) { return 'Review'; }
    return this.getResourceTypeLabel(this.reviewPendingDelete);
  }

  get pendingDeleteDisplayName(): string {
    if (!this.reviewPendingDelete) { return '-'; }
    return this.displayNameOf(this.reviewPendingDelete) || '-';
  }

  get pendingDeleteIsShared(): boolean {
    if (!this.reviewPendingDelete) { return false; }
    return this.isSharedReviewRequest(this.reviewPendingDelete);
  }

  get pendingDeleteModalTitle(): string {
    return 'Excluir review';
  }

  get pendingDeleteActionLabel(): string {
    return 'Excluir';
  }

  get pendingDeleteActionLoadingLabel(): string {
    return 'Excluindo...';
  }

  get pendingDeleteDescription(): string {
    return this.pendingDeleteIsShared
      ? 'Este review pode ser compartilhado. Se houver outros usuários associados, ele será removido apenas da sua lista. Se você for o último usuário associado, o review será excluído definitivamente.'
      : 'Este review não é compartilhado. A exclusão é definitiva e não poderá ser desfeita.';
  }

  // ─── Helpers (espelham smartReviewManager.ts / smartReviewManagerView.ts) ──

  getAssistantJobs(review: Review): SmartReviewJobFull[] {
    return (review.jobs?.assistants ?? []).filter(Boolean) as SmartReviewJobFull[];
  }

  getUpdatedAt(review: Review): string | undefined {
    const assistants = this.getAssistantJobs(review);
    const pickLatest = (dates: (string | undefined)[]): string | undefined =>
      dates
        .filter((d): d is string => !!d)
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    return (
      review.lastActivityAt ||
      pickLatest(assistants.map(a => a.updatedAt)) ||
      review.jobs?.review?.updatedAt ||
      pickLatest(assistants.map(a => a.createdAt)) ||
      review.jobs?.review?.createdAt ||
      review.requestedAt
    );
  }

  formatDateTime(value?: string, allowEmpty = false): string {
    return formatDateTimePtBr(value, allowEmpty);
  }

  getResourceTypeLabel(review: Review): string {
    return resourceTypeLabel(review.resourceType) || 'Review';
  }

  getSourceProviderLabel(provider?: string): string {
    return sourceProviderLabel(provider);
  }

  getStatusLabel(status?: string): string {
    if (!status) { return 'n/d'; }
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  getStepLabel(step?: string): string {
    if (!step) { return 'n/d'; }
    // assistant-pending não é um passo em andamento: o review terminou e a análise nunca
    // foi pedida. Capitalizar o valor cru mostraria "Assistant-pending".
    //
    // "Pendente" e não "Análise pendente": a coluna é estreita, e numa coluna chamada Passo,
    // ao lado de Review, Assistant e Complete, a palavra sozinha já diz que o ciclo parou
    // antes do fim. O detalhe explica o que falta, e é lá que a explicação cabe.
    if (step === 'assistant-pending') { return 'Pendente'; }
    return step.charAt(0).toUpperCase() + step.slice(1);
  }

  /**
   * Tooltip rico — espelha buildTooltip() do smartReviewManagerView.ts.
   */
  buildTooltip(review: Review): string {
    const name = this.displayNameOf(review);
    const status = this.getStatusLabel(review.statusDerived);
    const step = this.getStepLabel(review.currentStep);
    const requester = review.requestedBy || 'n/d';
    const requestedAt = this.formatDateTime(review.requestedAt);
    const lastRequestedAt = this.formatDateTime(review.lastRequestedAt);
    const lastActivity = this.formatDateTime(this.getUpdatedAt(review));
    const typeL = (review.resourceType || '').toLowerCase();
    const hideSourceInfo = typeL === 'issue' || typeL === 'changeset';
    const provider = this.getSourceProviderLabel(review.sourceProvider);

    const buildSection = (title: string, lines: string[]): string => [
      title,
      ...lines.map(line => `- ${line}`)
    ].join('\n');

    const reviewInfoLines = [
      `Review ID: ${review.resourceId || 'n/d'}`,
      hideSourceInfo ? '' : `Fonte: ${review.sourceName || 'n/d'}`,
      provider ? `Origem: ${provider}` : ''
    ].filter((line): line is string => !!line);

    const issueLines = [
      review.issueKey ? `Issue: ${review.issueKey}` : '',
      review.issueSummary ? `Resumo: ${review.issueSummary}` : '',
      review.issueStatus ? `Status Jira: ${review.issueStatus}` : ''
    ].filter((line): line is string => !!line);

    const lastRetryBy = review.jobs?.review?.lastRetryBy?.trim();
    const lastRetryAt = this.formatDateTime(review.jobs?.review?.lastRetryAt, true);
    const hasRetryBy = !!lastRetryBy;
    const showRetryUser = hasRetryBy && lastRetryBy !== (review.requestedBy || '').trim();
    const retryLine = !hasRetryBy && !lastRetryAt
      ? undefined
      : showRetryUser
        ? `Reprocessado por: @${lastRetryBy}${lastRetryAt ? ` em ${lastRetryAt}` : ''}`
        : `Reprocessado em: ${lastRetryAt || 'n/d'}`;

    const executionLines = [
      `Status: ${status}`,
      `Passo: ${step}`,
      `Solicitado por: ${requester}`,
      `Solicitado em: ${requestedAt}`,
      `Última interação: ${lastRequestedAt}`,
      `Última atividade: ${lastActivity}`,
      ...(retryLine ? [retryLine] : [])
    ];

    const sections: string[] = [
      `${this.getResourceTypeLabel(review)} · ${name}`,
      '',
      buildSection('Dados do review', reviewInfoLines),
      '',
      ...(issueLines.length ? [buildSection('Contexto Jira', issueLines), ''] : []),
      buildSection('Execução', executionLines)
    ];

    const reviewJob = review.jobs?.review;
    if (reviewJob) {
      const reviewJobLines = [
        `Status: ${this.getStatusLabel(reviewJob.status)}`,
        `Tentativas: ${reviewJob.attempts ?? 0}`
      ];
      if (reviewJob.error) { reviewJobLines.push(`Erro: ${reviewJob.error}`); }

      sections.push('');
      sections.push(buildSection('Job Review', reviewJobLines));
    }

    const assistants = this.getAssistantJobs(review);
    assistants.forEach((assistant, index) => {
      const suffix = assistants.length > 1 ? ` #${index + 1}` : '';
      const prompt = assistant.promptType ? ` (${assistant.promptType})` : '';
      const assistantLines = [
        `Status: ${this.getStatusLabel(assistant.status)}`,
        `Tentativas: ${assistant.attempts ?? 0}`
      ];
      if (assistant.error) { assistantLines.push(`Erro: ${assistant.error}`); }

      sections.push('');
      sections.push(buildSection(`Assistant Job${suffix}${prompt}`, assistantLines));
    });

    return sections.join('\n');
  }

  getStatusBadgeType(status?: string): string {
    return STATUS_BADGE_MAP[(status || '').toLowerCase()] ?? 'warning';
  }

  getStepBadgeType(step?: string, status?: string): string {
    const currentStep = (step || '').toLowerCase();
    const currentStatus = (status || '').toLowerCase();

    if (currentStatus === 'failed' || currentStatus === 'dead') { return 'danger'; }
    if (currentStep === 'complete' && currentStatus === 'done') { return 'success'; }
    if (currentStep === 'review' || currentStep === 'assistant') { return 'info'; }
    // Análise pendente não é sucesso nem erro: falta uma ação, e a cor tem de dizer isso.
    return 'warning';
  }

  getJobError(review: Review): string | undefined {
    const assistants = this.getAssistantJobs(review);
    return assistants.find(a => a.error)?.error || review.jobs?.review?.error;
  }

  private resolveComparisonTo(review: Pick<Review, 'resourceId' | 'to'>): number | undefined {
    if (Number.isFinite(review.to)) { return review.to as number; }

    const parts = (review.resourceId || '').split('_');
    if (parts.length < 2) { return undefined; }
    const parsedTo = Number(parts[1]);
    return Number.isFinite(parsedTo) ? parsedTo : undefined;
  }

  private isSharedReviewRequest(review: Pick<Review, 'resourceType' | 'resourceId' | 'to'>): boolean {
    const type = (review.resourceType || '').toLowerCase();
    if (type === 'issue' || type === 'changeset') { return true; }
    if (type === 'comparison') {
      const comparisonTo = this.resolveComparisonTo(review);
      return Number.isFinite(comparisonTo) && (comparisonTo as number) > 0;
    }
    return false;
  }

  private getDeleteBlockedMessage(review: Pick<Review, 'resourceType' | 'resourceId' | 'to'>): string {
    return 'Review em processamento não pode ser excluído. Aguarde a conclusão.';
  }

  private resolveStep(currentStep?: string, status?: string): string | undefined {
    if (currentStep?.trim()) { return currentStep; }

    const normalizedStatus = (status || '').toLowerCase();
    if (normalizedStatus === 'done') { return 'complete'; }
    return undefined;
  }

  private withComputedFields(reviews: Review[]): Review[] {
    return reviews.map(r => ({
      ...r,
      currentStep: this.resolveStep(r.currentStep, r.statusDerived),
      lastActivityAt: this.getUpdatedAt(r)
    }));
  }

  // ─── Data Loading ──────────────────────────────────────────────────────────

  loadReviews(showLoading = true): void {
    if (showLoading) { this.loading = true; }
    this.currentPage = 1;

    this.smartReviewService.getReviews(this.filters, this.pageSize, 0, this.sortBy, this.sortOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.reviews = this.withComputedFields(response.items || []);
          this.totalItems = response.total || 0;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationServiceHM.showToast('Erro ao carregar reviews', 'error');
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  viewDetail(item: Review): void {
    if (this.cannotViewDetail(item)) {
      this.notificationServiceHM.showToast(
        'Ver detalhes fica disponível quando o review termina de processar.',
        'warning'
      );
      return;
    }

    this.loading = true;
    const resolvedLastActivityAt = item.lastActivityAt ?? this.getUpdatedAt(item) ?? '';
    this.router.navigate(
      ['/menu/smart-review/reviews', item.resourceType, item.resourceId],
      {
        state: {
          requestedBy: item.requestedBy ?? '',
          requestedAt: item.requestedAt ?? '',
          lastActivityAt: resolvedLastActivityAt,
          lastRetryBy: item.jobs?.review?.lastRetryBy ?? '',
          lastRetryAt: item.jobs?.review?.lastRetryAt ?? '',
          sourceDisplayName: item.sourceDisplayName ?? '',
          sourceName: item.sourceName ?? '',
          sourceProvider: item.sourceProvider ?? '',
          statusDerived: item.statusDerived ?? '',
          currentStep: item.currentStep ?? '',
          reviewJobStatus: item.jobs?.review?.status ?? '',
          from: item.from ?? null,
          to: item.to ?? null
        }
      }
    );
  }


  cannotRetry(item: Review): boolean {
    return !TERMINAL_STATUSES.includes(item.statusDerived ?? '');
  }

  cannotDeltaReview(item: Review): boolean {
    if (item.resourceType !== 'issue') return true;
    const status = item.statusDerived ?? '';
    return !!status && !TERMINAL_STATUSES.includes(status);
  }

  openDeltaReviewModal(item: Review): void {
    if (this.cannotDeltaReview(item)) { return; }
    this.reviewPendingDelta = item;
    this.deltaReviewModal.open();
  }

  closeDeltaReviewModal(): void {
    this.reviewPendingDelta = null;
    this.deltaReviewModal.close();
  }

  confirmDeltaReview(): void {
    const item = this.reviewPendingDelta;
    this.deltaReviewModal.close();
    this.reviewPendingDelta = null;
    if (!item) { return; }
    this.onDeltaReview(item);
  }

  onDeltaReview(item: Review): void {
    this.loading = true;
    this.smartReviewService.submitIssueDelta(item.resourceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.loading = false;
          if (result.httpStatus === 200) {
            this.notificationServiceHM.showToast('Nenhum changeset novo encontrado para revisar', 'warning');
          } else {
            this.notificationServiceHM.showToast('Revisão incremental iniciada', 'success');
            this.loadReviewsAndPollPending();
          }
          this.cdr.markForCheck();
        },
        error: error => {
          this.loading = false;
          if (isServiceUnavailable(error)) {
            showServiceUnavailableToast(this.notificationServiceHM, error);
          } else {
            this.notificationServiceHM.showToast(getApiErrorMessage(error, 'Erro ao iniciar revisão incremental'), 'error');
          }
          this.cdr.markForCheck();
        }
      });
  }

  /** A regra mora em `reviewOpenBlock`, a mesma que a tela do review aplica. */
  cannotViewDetail(item: Review): boolean {
    return reviewOpenBlock(item.statusDerived, item.currentStep, this.permissions.hasGlobalView()) !== null;
  }


  private getReviewLifecycleStatus(item: Review): string {
    const derived = (item.statusDerived || '').toLowerCase().trim();
    if (derived) { return derived; }
    return (item.jobs?.review?.status || '').toLowerCase().trim();
  }

  private isTerminalReview(item: Review): boolean {
    const status = this.getReviewLifecycleStatus(item);
    return !!status && TERMINAL_STATUSES.includes(status);
  }

  cannotDeleteReview(item: Review): boolean {
    return this.deletingReviewKeys.has(this.getDeleteKey(item)) || !this.isTerminalReview(item);
  }

  openDeleteReviewModal(item: Review): void {
    if (this.cannotDeleteReview(item)) {
      if (!this.deletingReviewKeys.has(this.getDeleteKey(item))) {
        this.notificationServiceHM.showToast(
          this.getDeleteBlockedMessage(item),
          'warning'
        );
      }
      return;
    }
    this.reviewPendingDelete = item;
    this.deletingReview = false;
    this.updateDeleteReviewModalActions();
    this.deleteReviewModal.open();
  }

  closeDeleteReviewModal(): void {
    if (this.deletingReview) { return; }
    this.reviewPendingDelete = null;
    this.updateDeleteReviewModalActions();
    this.deleteReviewModal.close();
  }

  confirmDeleteReview(): void {
    const review = this.reviewPendingDelete;
    if (!review || this.deletingReview) { return; }
    if (!this.isTerminalReview(review)) {
      this.notificationServiceHM.showToast(
        this.getDeleteBlockedMessage(review),
        'warning'
      );
      this.closeDeleteReviewModal();
      return;
    }

    const deleteKey = this.getDeleteKey(review);
    if (this.deletingReviewKeys.has(deleteKey)) { return; }

    this.deletingReview = true;
    this.deletingReviewKeys.add(deleteKey);
    this.updateDeleteReviewModalActions();

    this.smartReviewService
      // `?requestedBy=` (remover review de outro usuário) só é aceito com `smartreview.admin`.
      .deleteReview(review.resourceType, review.resourceId, this.permissions.hasAdmin() ? review.requestedBy : undefined)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.deletingReview = false;
          this.deletingReviewKeys.delete(deleteKey);
          this.updateDeleteReviewModalActions();
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          const successMessage = this.isSharedReviewRequest(review)
            ? 'Review removido da sua lista'
            : 'Review excluído com sucesso';
          this.removeReviewFromList(review);
          this.totalItems = Math.max(this.totalItems - 1, 0);
          this.reviewPendingDelete = null;
          this.deleteReviewModal.close();
          this.reloadFilteredGridAfterDelete();
          this.notificationServiceHM.showToast(successMessage, 'success');
        },
        error: (error: unknown) => {
          const status = getDeleteReviewHttpStatus(error);
          // Erros definitivos (4xx) fecham o modal; falhas transitórias (5xx/rede) o mantêm aberto para retry.
          const isDefinitive = status !== null && status >= 400 && status < 500;
          if (isDefinitive) {
            if (status === 404) {
              this.removeReviewFromList(review);
              this.totalItems = Math.max(this.totalItems - 1, 0);
              this.reloadFilteredGridAfterDelete();
            }
            this.reviewPendingDelete = null;
            this.deleteReviewModal.close();
          }
          this.notificationServiceHM.showToast(getDeleteReviewErrorMessage(error), 'error');
        }
      });
  }

  openRetryModal(item: Review): void {
    this.retryModal.open(item);
  }

  onRetryConfirmed(req: RetryRequest): void {
    // Gerar não é reprocessar: não há job do assistente a repetir, e reexecutar o review
    // refaz o mesmo trabalho com o payload que ele já tinha — que não pedia a análise.
    if (req.kind === 'generate') {
      this.generateAnalysis(req);
      return;
    }
    this.jobsService.retryJob(req.jobId, req.rerunMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const modeLabel = req.rerunMode === 'missing' ? 'pendentes' : 'completo';
          this.notificationServiceHM.showToast(`Reprocessamento (${modeLabel}) do ${req.jobKey} iniciado`, 'success');
          this.startPolling(req.resourceId, req.resourceType);
          this.cdr.markForCheck();
        },
        error: error => {
          if (isServiceUnavailable(error)) {
            showServiceUnavailableToast(this.notificationServiceHM, error);
          } else {
            this.notificationServiceHM.showToast(getApiErrorMessage(error, 'Erro ao reprocessar'), 'error');
          }
          this.cdr.markForCheck();
        }
      });
  }

  /** Enfileira o assistente de code review de um review que ainda não tem análise. */
  private generateAnalysis(req: RetryRequest): void {
    this.smartReviewService.runAssistant(req.resourceType, req.resourceId, 'code_review', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationServiceHM.showToast('Análise solicitada.', 'success');
          this.startPolling(req.resourceId, req.resourceType);
          this.cdr.markForCheck();
        },
        error: error => {
          if (isServiceUnavailable(error)) {
            showServiceUnavailableToast(this.notificationServiceHM, error);
          } else {
            this.notificationServiceHM.showToast(getApiErrorMessage(error, 'Erro ao solicitar a análise'), 'error');
          }
          this.cdr.markForCheck();
        }
      });
  }

  onJobSubmitted(_result: { jobId: string }): void {
    // jobId não é usado: a resposta do submit não retorna resourceId/resourceType,
    // então recarregamos a lista e iniciamos polling em todos os itens não-terminais encontrados.
    this.loadReviewsAndPollPending();
  }

  private loadReviewsAndPollPending(): void {
    this.loading = true;
    this.smartReviewService.getReviews(this.filters, this.pageSize, 0, this.sortBy, this.sortOrder)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.reviews = this.withComputedFields(response.items || []);
          this.totalItems = response.total || 0;
          this.loading = false;
          this.reviews
            .filter(r => !TERMINAL_STATUSES.includes(r.statusDerived ?? ''))
            .forEach(r => this.startPolling(r.resourceId, r.resourceType));
          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationServiceHM.showToast('Erro ao carregar reviews', 'error');
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  startPolling(resourceId: string, resourceType: string): void {
    if (this.pollingActive.has(resourceId)) { return; }
    this.pollingActive.add(resourceId);
    let attempts = 0;
    this.smartReviewService.getReviewStatus(resourceId, resourceType).pipe(
      expand(review => {
        attempts++;
        const status = review?.statusDerived ?? '';
        const isTerminal = TERMINAL_STATUSES.includes(status);
        if (isTerminal || attempts >= MAX_POLL_ATTEMPTS) { return EMPTY; }
        return timer(this.getPollIntervalMs(attempts)).pipe(
          concatMap(() => this.smartReviewService.getReviewStatus(resourceId, resourceType))
        );
      }),
      takeWhile(review => !TERMINAL_STATUSES.includes(review?.statusDerived ?? ''), true),
      takeUntil(this.destroy$),
      catchError(() => of(null))
    ).subscribe({
      next: review => {
        if (!review) { return; }
        this.updateReviewInList(review);
        if (review.statusDerived === 'done') {
          this.notificationServiceHM.showToast('Review concluído!', 'success');
          this.loadReviews(false);
        } else if (FAILED_STATUSES.includes(review.statusDerived ?? '')) {
          const error = this.getJobError(review);
          this.notificationServiceHM.showToast(
            `Review falhou${error ? ': ' + error : ''}`, 'error'
          );
          this.loadReviews(false);
        }
        this.cdr.markForCheck();
      },
      complete: () => this.pollingActive.delete(resourceId),
      error: () => this.pollingActive.delete(resourceId)
    });
  }

  private updateReviewInList(review: Review): void {
    const idx = this.reviews.findIndex(r => r.resourceId === review.resourceId);
    if (idx === -1) { return; }
    const updated: Review = {
      ...this.reviews[idx],
      statusDerived: review.statusDerived,
      currentStep: this.resolveStep(review.currentStep, review.statusDerived),
      jobs: review.jobs,
      lastActivityAt: review.lastActivityAt
    };
    this.reviews = [
      ...this.reviews.slice(0, idx),
      { ...updated, lastActivityAt: this.getUpdatedAt(updated) },
      ...this.reviews.slice(idx + 1)
    ];
  }

  private getPollIntervalMs(attempts: number): number {
    if (attempts <= 3) { return POLL_INTERVAL_FAST_MS; }
    if (attempts <= 8) { return POLL_INTERVAL_MEDIUM_MS; }
    return POLL_INTERVAL_SLOW_MS;
  }

  private getDeleteKey(review: Pick<Review, 'resourceType' | 'resourceId'>): string {
    return buildReviewDeleteKey(review.resourceType, review.resourceId);
  }

  private removeReviewFromList(targetReview: Review): void {
    const targetKey = this.getDeleteKey(targetReview);
    let index = this.reviews.indexOf(targetReview);

    if (index === -1) {
      index = this.reviews.findIndex(review =>
        this.getDeleteKey(review) === targetKey
        && (review.requestId || '') === (targetReview.requestId || '')
        && (review.areaId || '') === (targetReview.areaId || '')
        && (review.versionFingerprint || '') === (targetReview.versionFingerprint || '')
        && (review.requestedAt || '') === (targetReview.requestedAt || '')
        && (review.requestedBy || '') === (targetReview.requestedBy || '')
      );
    }

    if (index === -1) {
      index = this.reviews.findIndex(review => this.getDeleteKey(review) === targetKey);
    }

    if (index === -1) { return; }

    const removed = this.reviews[index];
    this.pollingActive.delete(removed.resourceId);
    this.reviews = [
      ...this.reviews.slice(0, index),
      ...this.reviews.slice(index + 1)
    ];
  }

  private hasActiveGridFilters(): boolean {
    return !!this.filters.quickSearch?.trim()
      || (this.filters.resourceTypes?.length ?? 0) > 0
      || (this.filters.sourceProviders?.length ?? 0) > 0
      || (this.filters.statuses?.length ?? 0) > 0
      || !!this.filters.requestedBy?.trim()
      || !!this.filters.compareFrom?.trim()
      || !!this.filters.compareTo?.trim();
  }

  private reloadFilteredGridAfterDelete(): void {
    if (!this.hasActiveGridFilters()) { return; }
    this.loadReviews(false);
  }

  private updateDeleteReviewModalActions(): void {
    this.deleteReviewPrimaryAction = {
      ...this.deleteReviewPrimaryAction,
      label: this.deletingReview ? this.pendingDeleteActionLoadingLabel : this.pendingDeleteActionLabel,
      loading: this.deletingReview,
      disabled: this.deletingReview || !this.reviewPendingDelete || !this.isTerminalReview(this.reviewPendingDelete)
    };

    this.deleteReviewSecondaryAction = {
      ...this.deleteReviewSecondaryAction,
      disabled: this.deletingReview
    };
  }
  /**
   * Nome legível de um review na listagem.
   *
   * Pull Request chega com sourceName e sourceDisplayName VAZIOS: esses campos carregam
   * o nome do fonte, e a API os deixa vazios quando o recurso tem vários arquivos — a
   * mesma regra que já vale para changeset e issue. Sem montar o rótulo aqui, a linha
   * mostraria o resourceId cru (`pr:protheus:1`).
   */
  private displayNameOf(review: { resourceType?: string; sourceDisplayName?: string; resourceId?: string }): string {
    if (review.sourceDisplayName) { return review.sourceDisplayName; }
    if (review.resourceType === 'pull' && review.resourceId) {
      return pullResourceLabel(review.resourceId);
    }
    if (review.resourceType === 'commit' && review.resourceId) {
      return commitResourceLabel(review.resourceId);
    }
    return review.resourceId || 'sem nome';
  }

}
